import React, { useState } from 'react'
import { graphql, useStaticQuery } from 'gatsby'
import isEqual from 'lodash/isEqual'
import { getImage, GatsbyImage } from 'gatsby-plugin-image'
import PropTypes from 'prop-types'
import { Util, Transformation, icon, CRS, Point, bounds, LatLng, point, latLngBounds, latLng } from 'leaflet'
import { MapContainer, TileLayer, Marker, Popup, useMapEvents } from 'react-leaflet'
import classNames from 'classnames'
import 'leaflet-fullscreen/dist/Leaflet.fullscreen.min'
import 'leaflet-fullscreen/dist/leaflet.fullscreen.css'
import artifact from 'src/images/maps/teyvat/artifact.png'

const mapCenter = [3568, 6286]
const mapSize = [12288, 12288]

function LocationMarker() {
  const map = useMapEvents({
    click(e) {
      console.log(`[${e.latlng.lat}, ${e.latlng.lng}]`)
    },
  })
  return null
}

const renderartifactMarker = (artifactData, artifactIcon) => (
  <Marker icon={artifactIcon} position={artifactData.position}>
    <Popup className="w-80">
      <div>
        {artifactData.description}
        {artifactData.image && (
          <a
            href={artifactData.image.childImageSharp.gatsbyImageData.images.fallback.src}
            target="_blank"
            rel="noreferrer"
          >
            <GatsbyImage alt="artifact location" image={getImage(artifactData.image)} />
          </a>
        )}
        {/* {<ToggleDoneButton />} */}
      </div>
    </Popup>
  </Marker>
)

const ToggleDoneButton = (isDone) => {
  const [isToggleButtonDone, setIsToggleButtonDone] = useState(isDone)
  return (
    <div className="m-2 p-0.5 flex justify-evenly border-primary border-2 rounded-2xl overflow-hidden border-opacity-75">
      <button
        type="button"
        onClick={() => setIsToggleButtonDone(false)}
        className={classNames('flex flex-grow justify-center rounded-2xl focus:outline-none', {
          'bg-red-500': !isToggleButtonDone,
        })}
      >
        Todo
      </button>
      <button
        type="button"
        onClick={() => setIsToggleButtonDone(true)}
        className={classNames('flex flex-grow justify-center rounded-2xl focus:outline-none', {
          'bg-green-500': isToggleButtonDone,
        })}
      >
        Done
      </button>
    </div>
  )
}

const isDomAvailable = () => typeof window !== 'undefined' && !!window.document && !!window.document.createElement

const Map = () => {
  if (!isDomAvailable()) {
    return (
      <div>
        <p className="map-loading">Loading map...</p>
      </div>
    )
  }

  const {
    allFile: { edges: artifactImages },
  } = useStaticQuery(graphql`
    query {
      allFile(sort: { fields: name }, filter: { absolutePath: { regex: "images/maps/teyvat/artifacts/" } }) {
        edges {
          node {
            childImageSharp {
              gatsbyImageData(placeholder: NONE, height: 720, width: 1280)
            }
          }
        }
      }
    }
  `)

  const artifacts12h = [
    {
      description: '3 artifact spots - 12h respawn',
      image: artifactImages[9].node,
      position: [-955, -2540],
    },
    {
      description: '1 artifact spot - 12h respawn',
      image: artifactImages[11].node,
      position: [-1300, -2330],
    },
    {
      description: '1 artifact spot - 12h respawn',
      image: artifactImages[18].node,
      position: [-920, -1235],
    },
    {
      description: '1 artifact spot - 12h respawn',
      image: artifactImages[19].node,
      position: [-870, -1250],
    },
    {
      description: '1 artifact spot - 12h respawn',
      image: artifactImages[20].node,
      position: [-890, -1295],
    },
  ]

  const inazumaartifacts = [
    {
      description: '1 artifact spot',
      image: artifactImages[39].node,
      position: [5417, 2606],
    },
    {
      description: '1 artifact spot',
      image: artifactImages[40].node,
      position: [5805, 3438],
    },
    {
      description: '1 artifact spot',
      image: artifactImages[41].node,
      position: [5779, 3468],
    },
    {
      description: '1 artifact spot',
      image: artifactImages[42].node,
      position: [5844, 3478],
    },
    {
      description: '1 artifact spot',
      image: artifactImages[43].node,
      position: [5763, 3533],
    },
    {
      description: '2 artifact spots',
      image: artifactImages[44].node,
      position: [5238, 3632.5],
    },
    {
      description: '1 artifact spot',
      image: artifactImages[45].node,
      position: [5074, 3484],
    },
    {
      description: '1 artifact spot',
      image: artifactImages[46].node,
      position: [4878, 3513],
    },
    {
      description: '1 artifact spot',
      image: artifactImages[47].node,
      position: [4707, 3241],
    },
    {
      description: '1 artifact spot',
      image: artifactImages[48].node,
      position: [4712, 3203],
    },
    {
      description: '1 artifact spot',
      image: artifactImages[49].node,
      position: [4820, 3125],
    },
    {
      description: '2 artifact spots',
      image: artifactImages[50].node,
      position: [4899, 3127],
    },
    {
      description: '1 artifact spot',
      image: artifactImages[51].node,
      position: [4275, 3942],
    },
    {
      description: '1 artifact spot',
      image: artifactImages[52].node,
      position: [3873, 4098],
    },
    {
      description: '1 artifact spot',
      image: artifactImages[53].node,
      position: [3354, 4256],
    },
    {
      description: '1 artifact spot',
      image: artifactImages[54].node,
      position: [3320, 4197],
    },
    {
      description: '3 artifact spots',
      image: artifactImages[55].node,
      position: [5062, 3900],
    },
    {
      description: '3 artifact spots',
      image: artifactImages[56].node,
      position: [5110, 3920],
    },
    {
      description: '1 artifact spot',
      image: artifactImages[57].node,
      position: [4980, 4038],
    },
    {
      description: '1 artifact spot',
      image: artifactImages[58].node,
      position: [4862, 3953],
    },
    {
      description: '1 artifact spot',
      image: artifactImages[59].node,
      position: [4926.5, 3935],
    },
    {
      description: '1 artifact spot',
      image: artifactImages[60].node,
      position: [4682, 3950],
    },
    {
      description: '1 artifact spot',
      image: artifactImages[61].node,
      position: [4679, 4008],
    },
    {
      description: '1 artifact spot',
      image: artifactImages[62].node,
      position: [4566, 4020],
    },
    {
      description: '2 artifact spots',
      image: artifactImages[63].node,
      position: [4879, 4027],
    },
    {
      description: '2 artifact spots',
      image: artifactImages[64].node,
      position: [4803, 3991],
    },
    {
      description: '2 artifact spots',
      image: artifactImages[65].node,
      position: [4727, 4280],
    },
    {
      description: '3 artifact spots',
      image: artifactImages[66].node,
      position: [4734, 4147],
    },
    {
      description: '1 artifact spot',
      image: artifactImages[67].node,
      position: [4654, 4024],
    },
    {
      description: '3 artifact spots',
      image: artifactImages[68].node,
      position: [4726.5, 4090.5],
    },
    {
      description: '1 artifact spot',
      image: artifactImages[69].node,
      position: [4706.5, 4152],
    },
    {
      description: '1 artifact spot',
      image: artifactImages[70].node,
      position: [4719.5, 4113.5],
    },
    {
      description: '1 artifact spot',
      image: artifactImages[71].node,
      position: [4687.5, 4147.5],
    },
    {
      description: '1 artifact spot',
      image: artifactImages[72].node,
      position: [4687.5, 4135.5],
    },
  ]

  const artifacts24h = [
    {
      description: '1 artifact spot',
      image: artifactImages[0].node,
      position: [-497, -2251],
    },
    {
      description: '1 artifact spot',
      image: artifactImages[1].node,
      position: [-570, -1967],
    },
    {
      description: '1 artifact spot',
      image: artifactImages[2].node,
      position: [-327, -2077],
    },
    {
      description: '5 artifact spots',
      image: artifactImages[3].node,
      position: [-340, -1960],
    },
    {
      description: '1 artifact spot',
      image: artifactImages[4].node,
      position: [-242, -2037],
    },
    {
      description: '1 artifact spot',
      image: artifactImages[5].node,
      position: [-167, -2047],
    },
    {
      description: '1 artifact spot',
      image: artifactImages[6].node,
      position: [-360, -1718],
    },
    {
      description: '1 artifact spot',
      image: artifactImages[7].node,
      position: [-416, -1696],
    },
    {
      description: '1 artifact spot',
      image: artifactImages[8].node,
      position: [-440, -1700],
    },
    {
      description: '2 artifact spots',
      image: artifactImages[10].node,
      position: [-850, -2500],
    },
    {
      description: '2 artifact spots',
      image: artifactImages[12].node,
      position: [-1473, -2217],
    },
    {
      description: '2 artifact spots',
      image: artifactImages[13].node,
      position: [-1658, -2082],
    },
    {
      description: '1 artifact spot',
      image: artifactImages[14].node,
      position: [-1649, -1821],
    },
    {
      description: '2 artifact spots',
      image: artifactImages[15].node,
      position: [-915, -1783],
    },
    {
      description: '3 artifact spots',
      image: artifactImages[16].node,
      position: [-1038, -1593.5],
    },
    {
      description: '1 artifact spot',
      image: artifactImages[17].node,
      position: [-874, -1543],
    },
    {
      description: '1 artifact spot',
      image: artifactImages[21].node,
      position: [-593, 54],
    },
    {
      description: '1 artifact spot',
      image: artifactImages[22].node,
      position: [687.5, -1595],
    },
    {
      description: '3 artifact spots',
      image: artifactImages[23].node,
      position: [651, -2474.5],
    },

    {
      description: '2 artifact spots',
      image: artifactImages[24].node,
      position: [72, -2299],
    },
    {
      description: '1 artifact spot',
      image: artifactImages[25].node,
      position: [-4, -2080],
    },
    {
      description: '1 artifact spot',
      image: artifactImages[26].node,
      position: [-160, -3143],
    },
    {
      description: '1 artifact spot',
      image: artifactImages[27].node,
      position: [-299, -3109],
    },
    {
      description: '1 artifact spot',
      image: artifactImages[28].node,
      position: [-362, -3121],
    },
    {
      description: '1 artifact spot',
      image: artifactImages[29].node,
      position: [-628.5, -3147],
    },
    {
      description: '1 artifact spot',
      image: artifactImages[30].node,
      position: [183, -2917],
    },
    {
      description: '1 artifact spot',
      image: artifactImages[31].node,
      position: [376.5, -2793],
    },
    {
      description: '1 artifact spot',
      image: artifactImages[32].node,
      position: [730, -2835],
    },
    {
      description: '3 artifact spots',
      image: artifactImages[33].node,
      position: [1201, -2357],
    },
    {
      description: '4 artifact spots',
      image: artifactImages[34].node,
      position: [1578, -1987],
    },
    {
      description: '1 artifact spot',
      image: artifactImages[35].node,
      position: [133, -2025],
    },
    {
      description: '1 artifact spot',
      image: artifactImages[36].node,
      position: [69, -1718],
    },
    {
      description: '1 artifact spot',
      image: artifactImages[37].node,
      position: [246, -1197],
    },
    {
      description: '3 artifact spots',
      image: artifactImages[38].node,
      position: [-40, -1136],
    },
    ...inazumaartifacts,
  ]

  const artifactsFull = [...artifacts12h, ...artifacts24h]

  const [selectedRoute, setSelectedRoute] = useState(artifactsFull)

  const artifactIcon = icon({
    iconUrl: artifact,
    iconSize: [30, 30],
  })

  const CRSInvertedY = Util.extend({}, CRS.Simple, {
    transformation: new Transformation(1, 0, 1, 0),
    projection: {
      project(latlng) {
        return new Point(latlng.lat + mapCenter[0], latlng.lng + mapCenter[1])
      },
      unproject(unprojectPoint) {
        return new LatLng(unprojectPoint.x - mapCenter[0], unprojectPoint.y - mapCenter[1])
      },
    },
    bounds: bounds(point(0, 0), point(mapSize[0], mapSize[1])),
  })

  return (
    <div>
      <MapContainer
        className="h-screen my-4"
        center={[-528, -142]}
        crs={CRSInvertedY}
        zoom={-3}
        maxZoom={1}
        minZoom={-3}
        scrollWheelZoom={false}
        maxBounds={latLngBounds(
          latLng(-mapCenter[0], -mapCenter[1]),
          latLng(mapSize[0] - mapCenter[0], mapSize[1] - mapCenter[1])
        )}
        maxBoundsViscosity={0.5}
        attributionControl={false}
        fullscreenControl
      >
        <LocationMarker />
        <div className="flex mt-2 mr-2 flex-col absolute top-1 right-1 z-400">
          <button
            type="button"
            className={classNames(
              'px-4 py-2 rounded',
              isEqual(selectedRoute, artifactsFull) ? 'bg-secondary' : 'bg-blue-900'
            )}
            onClick={() => setSelectedRoute(artifactsFull)}
          >
            24h + 12h route
          </button>
          <button
            type="button"
            className={classNames(
              'mt-2 px-4 py-2 rounded',
              isEqual(selectedRoute, artifacts24h) ? 'bg-secondary' : 'bg-blue-900'
            )}
            onClick={() => setSelectedRoute(artifacts24h)}
          >
            24h route
          </button>
          <button
            type="button"
            className={classNames(
              'mt-2 px-4 py-2 rounded',
              isEqual(selectedRoute, artifacts12h) ? 'bg-secondary' : 'bg-blue-900'
            )}
            onClick={() => setSelectedRoute(artifacts12h)}
          >
            12h route
          </button>
        </div>
        {selectedRoute.map((artifactData) => renderartifactMarker(artifactData, artifactIcon))}
        <TileLayer
          url="/maps/teyvat/{z}/{x}_{y}.jpg"
          continuousWorld={false}
          noWrap
          maxZoom={10}
          minZoom={-6}
          maxNativeZoom={0}
          minNativeZoom={-3}
          zoomControl={false}
          updateInterval={200}
        />
      </MapContainer>
    </div>
  )
}

export default Map
